import { useMemo, useState } from "react";
import { api } from "@/lib/api";

const PHASES = ["desarrollo", "preproducción", "rodaje", "postproducción", "distribución"];
const PHASE_COLORS = {
  "desarrollo": "#C9821B",
  "preproducción": "#5B7A8E",
  "rodaje": "#941620",
  "postproducción": "#2E7D5B",
  "distribución": "#7C4D9C",
};

const fmtDateRange = (a, b) => {
  if (!a) return "";
  const s = new Date(a).toLocaleDateString("es-ES", { day: "2-digit", month: "short" });
  if (!b) return s;
  const e = new Date(b).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  return `${s} → ${e}`;
};

export default function CalendarTab({ project, onChange, onSave }) {
  const calendar = project.calendar || { blocks: [], notes: "" };
  const blocks = calendar.blocks || [];

  const setCal = (c) => onChange({ ...project, calendar: c });

  const addBlock = () => setCal({
    ...calendar,
    blocks: [
      ...blocks,
      {
        id: Date.now().toString(),
        title: "Nuevo bloque",
        phase: "rodaje",
        start_date: "",
        end_date: "",
        days: 0,
        locations: "",
        notes: "",
        color: PHASE_COLORS.rodaje,
      },
    ],
  });
  const upd = (i, patch) => {
    const next = blocks.map((b, j) => {
      if (j !== i) return b;
      const merged = { ...b, ...patch };
      if (patch.phase) merged.color = PHASE_COLORS[patch.phase] || merged.color;
      return merged;
    });
    setCal({ ...calendar, blocks: next });
  };
  const del = (i) => setCal({ ...calendar, blocks: blocks.filter((_, j) => j !== i) });
  const move = (i, dir) => {
    const arr = [...blocks];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    setCal({ ...calendar, blocks: arr });
  };

  // ---- Compute timeline bars ----
  const { minD, maxD, totalDays } = useMemo(() => {
    const valid = blocks.filter((b) => b.start_date && b.end_date);
    if (!valid.length) return { minD: null, maxD: null, totalDays: 0 };
    const dates = valid.flatMap((b) => [new Date(b.start_date), new Date(b.end_date)]);
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    const days = Math.max(1, Math.ceil((max - min) / 86400000));
    return { minD: min, maxD: max, totalDays: days };
  }, [blocks]);

  const totals = useMemo(() => {
    const out = {};
    PHASES.forEach((p) => (out[p] = { count: 0, days: 0 }));
    blocks.forEach((b) => {
      const p = b.phase || "rodaje";
      if (!out[p]) out[p] = { count: 0, days: 0 };
      out[p].count += 1;
      out[p].days += Number(b.days || 0);
    });
    return out;
  }, [blocks]);

  const totalShootDays = totals["rodaje"]?.days || 0;
  const [suggesting, setSuggesting] = useState(false);
  const [suggestMsg, setSuggestMsg] = useState("");

  const suggestPlan = async (mode) => {
    setSuggesting(true);
    setSuggestMsg("");
    try {
      const r = await api.post(`/icaa/projects/${project.id}/ai/calendar/suggest`, {}, { timeout: 240000 });
      const incoming = r.data.blocks || [];
      const next = mode === "replace" ? incoming : [...blocks, ...incoming];
      setCal({ ...calendar, blocks: next });
      setSuggestMsg(`✓ ${incoming.length} bloques de rodaje generados (sin fechas — añádelas tú).`);
      setTimeout(() => onSave?.(), 200);
    } catch (e) {
      setSuggestMsg("Error: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <section className="mx-auto max-w-7xl px-4 py-6 sm:px-8 sm:py-8" data-testid="calendar-section">
      {/* AI suggest bar */}
      <div className="mb-5 rounded-xl border border-gb-red/20 bg-gb-red/5 p-3 sm:p-4" data-testid="suggest-bar">
        <div className="flex flex-wrap items-center gap-2">
          <p className="mr-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-gb-red">Plan de rodaje sugerido por IA:</p>
          <button onClick={() => suggestPlan("append")} disabled={suggesting} data-testid="suggest-append-btn"
                  className="rounded-full bg-gb-black px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white hover:bg-gb-red disabled:opacity-40">
            {suggesting ? "Pensando…" : "Sugerir bloques"}
          </button>
          <button onClick={() => suggestPlan("replace")} disabled={suggesting} data-testid="suggest-replace-btn"
                  className="rounded-full border border-gb-paper bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-gb-black/70 hover:border-gb-black/40 disabled:opacity-40">
            Reemplazar
          </button>
          <span className="text-[11px] italic text-gb-muted">Agrupa escenas por localización y día/noche. Tú pones las fechas después.</span>
        </div>
        {suggestMsg && <p className="mt-2 text-xs text-gb-black/70" data-testid="suggest-msg">{suggestMsg}</p>}
      </div>

      {/* Stats header */}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        {PHASES.map((p) => (
          <div key={p} className="rounded-xl border border-gb-paper bg-gb-surface p-4">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: PHASE_COLORS[p] }} />
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-gb-muted">{p}</p>
            </div>
            <p className="mt-1 gb-display text-2xl">{totals[p].count}</p>
            <p className="text-xs text-gb-muted">
              {totals[p].days ? `${totals[p].days} días` : "—"}
            </p>
          </div>
        ))}
      </div>

      {/* Timeline */}
      {minD && (
        <div className="mb-6 rounded-xl border border-gb-paper bg-gb-surface p-5" data-testid="calendar-timeline">
          <div className="mb-3 flex items-end justify-between">
            <h3 className="font-serif text-lg">Línea temporal</h3>
            <p className="text-xs text-gb-muted">
              {fmtDateRange(minD.toISOString(), maxD.toISOString())} · {totalDays} días naturales · {totalShootDays} jornadas de rodaje
            </p>
          </div>
          <div className="space-y-1.5">
            {blocks.filter((b) => b.start_date && b.end_date).map((b) => {
              const s = new Date(b.start_date);
              const e = new Date(b.end_date);
              const left = Math.max(0, ((s - minD) / (maxD - minD)) * 100);
              const w = Math.max(0.8, ((e - s) / (maxD - minD)) * 100);
              return (
                <div key={b.id} className="relative h-7" data-testid={`timeline-bar-${b.id}`}>
                  <div className="absolute top-1 h-5 rounded"
                       style={{ left: `${left}%`, width: `${w}%`, background: b.color || PHASE_COLORS[b.phase] || "#941620" }}
                       title={`${b.title} · ${fmtDateRange(b.start_date, b.end_date)}`} />
                  <span className="absolute -top-0 z-10 ml-1 truncate text-[11px] font-medium text-white mix-blend-difference"
                        style={{ left: `${left}%`, maxWidth: `${w}%` }}>
                    {b.title}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex justify-between text-[10px] uppercase tracking-widest text-gb-muted">
            <span>{minD.toLocaleDateString("es-ES", { month: "short", year: "numeric" })}</span>
            <span>{maxD.toLocaleDateString("es-ES", { month: "short", year: "numeric" })}</span>
          </div>
        </div>
      )}

      {/* Blocks table */}
      <div className="overflow-x-auto rounded-xl border border-gb-paper bg-gb-surface">
        <table className="w-full min-w-[820px] text-sm">
          <thead>
            <tr className="bg-gb-black text-left text-xs uppercase tracking-widest text-white">
              <th className="w-2 px-2" />
              <th className="px-3 py-3">Bloque</th>
              <th className="w-36 px-3 py-3">Fase</th>
              <th className="w-32 px-3 py-3">Inicio</th>
              <th className="w-32 px-3 py-3">Fin</th>
              <th className="w-24 px-3 py-3 text-right">Días</th>
              <th className="px-3 py-3">Localizaciones / notas</th>
              <th className="w-20 px-3 py-3" />
            </tr>
          </thead>
          <tbody>
            {blocks.map((b, i) => (
              <tr key={b.id} className="border-t border-gb-paper" data-testid={`cal-row-${i}`}>
                <td className="px-2" style={{ background: b.color || PHASE_COLORS[b.phase] }} />
                <td className="px-3 py-2">
                  <input value={b.title} onChange={(e) => upd(i, { title: e.target.value })} onBlur={onSave}
                         data-testid={`cal-title-${i}`}
                         className="w-full bg-transparent font-serif outline-none focus:border-b focus:border-gb-red" />
                </td>
                <td className="px-3 py-2">
                  <select value={b.phase} onChange={(e) => upd(i, { phase: e.target.value })} onBlur={onSave}
                          data-testid={`cal-phase-${i}`}
                          className="bg-transparent text-xs uppercase outline-none">
                    {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2">
                  <input type="date" value={b.start_date || ""} onChange={(e) => upd(i, { start_date: e.target.value })} onBlur={onSave}
                         data-testid={`cal-start-${i}`}
                         className="bg-transparent text-xs outline-none" />
                </td>
                <td className="px-3 py-2">
                  <input type="date" value={b.end_date || ""} onChange={(e) => upd(i, { end_date: e.target.value })} onBlur={onSave}
                         data-testid={`cal-end-${i}`}
                         className="bg-transparent text-xs outline-none" />
                </td>
                <td className="px-3 py-2 text-right">
                  <input type="number" value={b.days} onChange={(e) => upd(i, { days: Number(e.target.value) || 0 })} onBlur={onSave}
                         data-testid={`cal-days-${i}`}
                         className="w-16 bg-transparent text-right tabular-nums outline-none" />
                </td>
                <td className="px-3 py-2">
                  <input value={b.locations} onChange={(e) => upd(i, { locations: e.target.value })} onBlur={onSave}
                         placeholder="Lugar" data-testid={`cal-loc-${i}`}
                         className="w-full bg-transparent text-xs outline-none focus:border-b focus:border-gb-red" />
                  {(b.notes || b.id) && (
                    <input value={b.notes || ""} onChange={(e) => upd(i, { notes: e.target.value })} onBlur={onSave}
                           placeholder="Notas" data-testid={`cal-notes-${i}`}
                           className="mt-1 w-full bg-transparent text-[11px] italic text-gb-muted outline-none" />
                  )}
                </td>
                <td className="px-2 text-right">
                  <button onClick={() => move(i, -1)} className="px-1 text-gb-muted hover:text-gb-black">↑</button>
                  <button onClick={() => move(i, 1)} className="px-1 text-gb-muted hover:text-gb-black">↓</button>
                  <button onClick={() => del(i)} data-testid={`cal-del-${i}`}
                          className="px-1 text-gb-muted hover:text-gb-red">×</button>
                </td>
              </tr>
            ))}
            {!blocks.length && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-gb-muted">
                Aún no hay bloques. Crea el primero.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      <button onClick={addBlock} data-testid="cal-add"
              className="mt-3 w-full rounded-xl border-2 border-dashed border-gb-paper bg-gb-surface/60 py-4 text-sm font-semibold uppercase tracking-widest text-gb-muted hover:border-gb-red hover:text-gb-red">
        + Añadir bloque
      </button>

      <textarea value={calendar.notes || ""} onChange={(e) => setCal({ ...calendar, notes: e.target.value })} onBlur={onSave}
                data-testid="cal-notes"
                rows={3} placeholder="Notas del calendario (clima, ventanas estacionales, dependencias…)"
                className="mt-6 w-full resize-y rounded-md border border-gb-paper bg-gb-paper-soft/50 p-3 text-sm outline-none focus:border-gb-red" />
    </section>
  );
}
